import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '#environments';
import {
  WbCaseDetail,
  WbSubsurfaceCostEstimateDetail,
  SaveWbCostEstimateRequest,
  WbSubsurfaceProcedureDetail,
  SaveWbProcedureRequest,
  WbPermitApprovalDetail,
  SaveWbPermitApprovalRequest,
  WbStageQueueItem,
  CpAttachment,
  CpCorrespondenceItem,
  ProcedureTemplateOption,
  AuditLogEntry,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class WbCasesApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = `${environment.apiUrl}/wb-cases`;

  // ─── Case ─────────────────────────────────────────────────────────────────

  getCase(id: number): Observable<WbCaseDetail> {
    return this._http.get<WbCaseDetail>(`${this._baseUrl}/${id}`);
  }

  // ─── Subsurface Cost Estimate stage ──────────────────────────────────────

  getCostEstimate(id: number): Observable<WbSubsurfaceCostEstimateDetail> {
    return this._http.get<WbSubsurfaceCostEstimateDetail>(`${this._baseUrl}/${id}/cost-estimate`);
  }

  saveCostEstimate(id: number, request: SaveWbCostEstimateRequest, submit = false): Observable<WbCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<WbCaseDetail>(`${this._baseUrl}/${id}/cost-estimate`, request, { params });
  }

  // ─── Short Procedure stage ────────────────────────────────────────────────

  /** GET /api/wb-cases/procedure-templates — loads templates with content for Short Procedure dropdown (FR-128). */
  getProcedureTemplates(): Observable<ProcedureTemplateOption[]> {
    return this._http.get<ProcedureTemplateOption[]>(`${this._baseUrl}/procedure-templates`);
  }

  getProcedure(id: number): Observable<WbSubsurfaceProcedureDetail> {
    return this._http.get<WbSubsurfaceProcedureDetail>(`${this._baseUrl}/${id}/procedure`);
  }

  saveProcedure(id: number, request: SaveWbProcedureRequest, submit = false): Observable<WbCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<WbCaseDetail>(`${this._baseUrl}/${id}/procedure`, request, { params });
  }

  procedureBack(id: number, request: SaveWbProcedureRequest): Observable<WbCaseDetail> {
    return this._http.post<WbCaseDetail>(`${this._baseUrl}/${id}/procedure/back`, request);
  }

  /** POST /api/wb-cases/{id}/attachments — uploads a procedure-stage attachment (§9.2.17.4). */
  uploadProcedureAttachment(id: number, file: File, attachmentType: string): Observable<CpAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachmentType', attachmentType);
    return this._http.post<CpAttachment>(`${this._baseUrl}/${id}/attachments`, formData);
  }

  /** GET /api/wb-cases/{id}/attachments — lists procedure-stage attachments (§9.2.17.4). */
  getProcedureAttachments(id: number): Observable<CpAttachment[]> {
    return this._http.get<CpAttachment[]>(`${this._baseUrl}/${id}/attachments`);
  }

  // ─── Permit Approval stage ────────────────────────────────────────────────

  getPermitApproval(id: number): Observable<WbPermitApprovalDetail> {
    return this._http.get<WbPermitApprovalDetail>(`${this._baseUrl}/${id}/permit-approval`);
  }

  savePermitApproval(id: number, request: SaveWbPermitApprovalRequest, submit = false): Observable<WbCaseDetail> {
    let params = new HttpParams();
    if (submit) params = params.set('submit', true);
    return this._http.put<WbCaseDetail>(`${this._baseUrl}/${id}/permit-approval`, request, { params });
  }

  /**
   * POST /api/wb-cases/{id}/edit-procedure
   * Transitions the WB case from Permit Approval back to Subsurface Procedure (CH-S03-002, FR-157).
   * Caller must be Admin, PAEngineer, or PACoordinator.
   */
  editProcedure(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/edit-procedure`, {});
  }

  // ─── Audit Log ────────────────────────────────────────────────────────────

  /** GET /api/wb-cases/{id}/audit-log — returns audit log entries for the WB case, newest-first. */
  getAuditLog(id: number): Observable<AuditLogEntry[]> {
    return this._http.get<AuditLogEntry[]>(`${this._baseUrl}/${id}/audit-log`).pipe(
      map(entries => entries.map(e => ({ ...e, displayDescription: e.actionType }))),
    );
  }

  // ─── Correspondence ───────────────────────────────────────────────────────

  /** GET /api/wb-cases/{id}/correspondence — returns sent notification log entries for the WB case, newest-first. */
  getCorrespondence(id: number): Observable<CpCorrespondenceItem[]> {
    return this._http.get<CpCorrespondenceItem[]>(`${this._baseUrl}/${id}/correspondence`);
  }

  /** GET /api/wb-cases/{id}/correspondence/{logId}/download — returns a SAS URL for an archived .eml file. */
  getCorrespondenceDownloadUrl(id: number, logId: number): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/${id}/correspondence/${logId}/download`);
  }

  // ─── Stage-queue (6.2B) ──────────────────────────────────────────

  /** GET /api/wb-cases/stage-queue?stage={stage} — returns WB cases at the given workflow stage. */
  getStageQueue(stage: string): Observable<WbStageQueueItem[]> {
    return this._http.get<WbStageQueueItem[]>(`${this._baseUrl}/stage-queue`, {
      params: { stage },
    });
  }
}
